import React from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Home } from 'lucide-react';

const SITE_URL = 'https://traderadarglobal.com';

/**
 * Route label mapping – maps path segments to human-readable names.
 * Dynamic segments (e.g. :id) are handled separately via context or param props.
 */
const ROUTE_LABELS = {
  '': 'Home',
  countries: 'Countries',
  tariffs: 'Tariffs & Trade Events',
  'trade-deals': 'Trade Deals',
  legislation: 'Legislation',
  compare: 'Compare Countries',
  'middle-east-impact-dashboard': 'Iran Conflict Dashboard',
  insights: 'Insights',
  'strait-of-hormuz': 'Strait of Hormuz',
  about: 'About',
  contact: 'Contact',
  'privacy-policy': 'Privacy Policy',
  'terms-and-conditions': 'Terms & Conditions',
  'editorial-standards': 'Editorial standards',
};

/**
 * Breadcrumbs component with visible UI and BreadcrumbList JSON-LD.
 *
 * Renders breadcrumb navigation below the Navbar on every page except the homepage.
 * Injects BreadcrumbList structured data for Google rich results.
 *
 * For dynamic routes (e.g. /countries/5), pass `dynamicLabel` via a parent wrapper
 * or it will show a generic label. Pages can override this by passing context.
 */
const Breadcrumbs = ({ dynamicLabel }) => {
  const location = useLocation();
  const countryDetailMatch = useMatch({ path: '/countries/:id', end: true });
  const pathSegments = location.pathname.split('/').filter(Boolean);

  // Don't render breadcrumbs on the homepage
  if (pathSegments.length === 0) return null;

  // Build breadcrumb items
  const crumbs = [
    { label: 'Home', path: '/' },
  ];

  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === pathSegments.length - 1;

    // Check if this is a known route
    if (ROUTE_LABELS[segment]) {
      crumbs.push({ label: ROUTE_LABELS[segment], path: currentPath });
    } else if (countryDetailMatch && segment === countryDetailMatch.params.id) {
      crumbs.push({ label: 'Country trade profile', path: currentPath });
    } else if (isLast && dynamicLabel) {
      // Dynamic segment with a provided label
      crumbs.push({ label: dynamicLabel, path: currentPath });
    } else {
      // Fallback: capitalize the segment
      const label = segment
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      crumbs.push({ label, path: currentPath });
    }
  });

  // JSON-LD BreadcrumbList structured data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.label,
      item: `${SITE_URL}${crumb.path}`,
    })),
  };

  const hideVisibleNav = location.pathname === '/strait-of-hormuz';

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>
      <nav
        aria-label="Breadcrumb"
        className={`mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8${hideVisibleNav ? ' hidden' : ''}`}
      >
        <ol className="flex flex-wrap items-center text-sm text-slate-500 dark:text-on-surface-variant">
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <li key={crumb.path} className="flex items-center">
                {index > 0 && (
                  <ChevronRight className="mx-2 h-4 w-4 shrink-0 text-slate-400 dark:text-outline" />
                )}
                {isLast ? (
                  <span
                    className="font-medium text-slate-900 dark:text-on-surface"
                    aria-current="page"
                  >
                    {index === 0 && <Home className="mr-1 inline h-4 w-4" />}
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="transition-colors hover:text-primary-600 dark:hover:text-primary"
                  >
                    {index === 0 && <Home className="mr-1 inline h-4 w-4" />}
                    {crumb.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumbs;


